import React, { useMemo } from 'react';
import { useFocusIdx } from '@bay-email/editor';
import { SelectField } from '../../../components/Form';
import { t } from '@bay-email/core';
import {
	BsDashLg,
	BsThreeDots,
	BsPause,
	BsDistributeVertical,
	BsBoxArrowInDown,
	BsBoxArrowUp,
} from 'react-icons/bs';

type BorderStyleOption = {
	value: string;
	label: React.ReactNode;
	selectedLabel: React.ReactNode;
};

function selectedValueLabel(icon: React.ReactNode, title: string) {
	return (
		<span
			title={title}
			aria-label={title}
			style={{
				display: 'inline-flex',
				alignItems: 'center',
				justifyContent: 'center',
				width: '100%',
				minHeight: 20,
				color: 'rgba(78, 89, 105, 1)',
			}}
		>
			{icon}
		</span>
	);
}

function menuLabel(icon: React.ReactNode, title: string) {
	return (
		<span
			title={title}
			aria-label={title}
			style={{
				display: 'inline-flex',
				alignItems: 'center',
				gap: 8,
				width: '100%',
				color: 'rgba(78, 89, 105, 1)',
			}}
		>
			<span
				style={{
					display: 'inline-flex',
					alignItems: 'center',
					justifyContent: 'center',
					minWidth: 20,
					minHeight: 20,
				}}
			>
				{icon}
			</span>
			<span>{title}</span>
		</span>
	);
}

export const borderStyleOptions: BorderStyleOption[] = [
	{
		value: 'dashed',
		label: menuLabel(
			<span style={{ display: 'inline-flex', gap: 2 }}>
				<BsDashLg />
				<BsDashLg />
			</span>,
			t('Dashed'),
		),
		selectedLabel: selectedValueLabel(
			<span style={{ display: 'inline-flex', gap: 2 }}>
				<BsDashLg />
				<BsDashLg />
			</span>,
			t('Dashed'),
		),
	},
	{
		value: 'dotted',
		label: menuLabel(<BsThreeDots />, t('Dotted')),
		selectedLabel: selectedValueLabel(<BsThreeDots />, t('Dotted')),
	},
	{
		value: 'solid',
		label: menuLabel(<BsDashLg />, t('Solid')),
		selectedLabel: selectedValueLabel(<BsDashLg />, t('Solid')),
	},
	{
		value: 'double',
		label: menuLabel(
			<span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 0.7 }}>
				<BsDashLg />
				<BsDashLg />
			</span>,
			t('Double'),
		),
		selectedLabel: selectedValueLabel(
			<span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 0.7 }}>
				<BsDashLg />
				<BsDashLg />
			</span>,
			t('Double'),
		),
	},
	{
		value: 'ridge',
		label: menuLabel(<BsDistributeVertical />, t('Ridge')),
		selectedLabel: selectedValueLabel(<BsDistributeVertical />, t('Ridge')),
	},
	{
		value: 'groove',
		label: menuLabel(<BsPause style={{ transform: 'rotate(90deg)' }} />, t('Groove')),
		selectedLabel: selectedValueLabel(
			<BsPause style={{ transform: 'rotate(90deg)' }} />,
			t('Groove'),
		),
	},
	{
		value: 'inset',
		label: menuLabel(<BsBoxArrowInDown />, t('Inset')),
		selectedLabel: selectedValueLabel(<BsBoxArrowInDown />, t('Inset')),
	},
	{
		value: 'outset',
		label: menuLabel(<BsBoxArrowUp />, t('Outset')),
		selectedLabel: selectedValueLabel(<BsBoxArrowUp />, t('Outset')),
	},
];

export function BorderStyle({ secondary }: { secondary?: boolean }) {
	const { focusIdx } = useFocusIdx();

	return useMemo(() => {
		const renderFormat = (optionNode: React.ReactNode, value?: string | number) => {
			const selectedOption = borderStyleOptions.find(item => item.value === value);
			return selectedOption?.selectedLabel ?? optionNode;
		};

		return (
			<SelectField
				label={t('Style')}
				secondaryLabel={secondary}
				name={`${focusIdx}.attributes.border-style`}
				options={borderStyleOptions}
				placeholder=''
				renderFormat={renderFormat}
				triggerProps={{
					autoFitPopupMinWidth: false,
					popupStyle: {
						minWidth: 136,
					},
				}}
			/>
		);
	}, [focusIdx, secondary]);
}
